import {
  DataTableDataSearchDto,
  DatatableDataUserGroupSelectionDto,
  SortColumnDto,
  TableCacheKeyDto,
  TableDataDtoResponse,
} from '@asset-metrix/portal-gateway-client-js';
import {
  keepPreviousData,
  QueryFunction,
  useQuery,
} from '@tanstack/react-query';
import uniqBy from 'lodash/uniqBy';

import { extractErrorMessage } from '../../utils/errorMessage';
import {
  prependRoleId,
  QueryKeyWithRole,
  unwrapQueryKeyWithRole,
} from '../../utils/prependRoleId';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

export type FetchDatatableDataQueryKeyParams = {
  page: number;
  pageSize: number;
  pageFilter: DataTableDataSearchDto['selection'];
  tableFilter: DataTableDataSearchDto['selection'];
  workflowStateSelection?: DataTableDataSearchDto['workflowStateSelection'];
  sortItems: SortColumnDto[];
  userGroupSelection?: DatatableDataUserGroupSelectionDto['userGroupSelection'];
  stakeAdjusted?: boolean;
};

export type FetchDatatableDataQueryKey = readonly [
  TableCacheKeyDto,
  { pageId: string },
  { widgetId: string },
  FetchDatatableDataQueryKeyParams,
  ...string[],
];

export const fetchDatatableData: QueryFunction<
  TableDataDtoResponse,
  QueryKeyWithRole<FetchDatatableDataQueryKey>
> = async ({ queryKey }) => {
  const unwrappedQueryKey = unwrapQueryKeyWithRole(queryKey).params;
  const { widgetId } = unwrappedQueryKey[2];
  const {
    page,
    pageSize,
    pageFilter,
    tableFilter,
    workflowStateSelection,
    sortItems,
    userGroupSelection,
    stakeAdjusted,
  } = unwrappedQueryKey[3];

  const filterSelections = {
    ...pageFilter,
    ...tableFilter,
  };

  const sort = convertSortItemsToSortString(sortItems);
  const response = await portalGatewayClient.table.searchTableData(
    widgetId,
    {
      offset: page * pageSize,
      limit: pageSize,
      ...(sort != null && { sort }),
    },
    {
      selection: filterSelections,
      workflowStateSelection,
      userGroupSelection,
      stakeAdjusted,
    }
  );
  if (!response.ok) {
    throw new HttpError(extractErrorMessage(response.error), response);
  }

  return response.data;
};

export const useTableDataQuery = (
  pageId: string,
  widgetId: string,
  cacheKey: TableCacheKeyDto,
  queryParams: FetchDatatableDataQueryKeyParams,
  enabled: boolean
) =>
  useQuery({
    queryKey: prependRoleId<FetchDatatableDataQueryKey>([
      'datatables',
      cacheKey,
      { pageId },
      { widgetId },
      queryParams,
    ]),
    queryFn: fetchDatatableData,
    placeholderData: keepPreviousData,
    enabled,
  });

/**
 * @param {SortColumnDto[]} sortItems - e.g. [{key: 'status_published', sort: 'asc'},{key: 'file_name', sort: 'asc'},{key: 'created_at', sort: 'desc'}]
 * @returns {string} sortStr - A sort string, e.g. status_published,asc;file_name,asc;created_at,desc
 */
export function convertSortItemsToSortString(sortItems: SortColumnDto[]) {
  if (sortItems.length === 0) {
    return undefined;
  }

  return uniqBy(sortItems, 'key')
    .map(({ key, sort }) => `${key},${sort}`)
    .join(';');
}
